import React, { useState, useRef, useMemo } from 'react';
import {
  Overlay,
  Modal,
  Box,
  Text,
  Button,
  Input,
  Icon,
  ListGroup,
  Loading,
} from '@cubeyou-team/hx-react-components';
import { StyledDragWrapper, StyledDragFileName } from './StyledCreateTsvModal';

const initialName = {
  value: '',
  error: false,
  touched: false,
};

const initialFile = {
  value: null,
  error: false,
  touched: false,
};

const CreateTsvModal = ({ onClose, onCreate, error, loading }) => {
  const [name, setName] = useState(initialName);
  const [file, setFile] = useState(initialFile);
  const [dragging, setDragging] = useState(false);
  const formError = useMemo(() => {
    return name.error || file.error || !name.touched || !file.touched;
  }, [name, file]);
  const input = useRef();
  const onChangeName = e => {
    e.persist();
    const newName = e.target.value;
    setName({ touched: true, value: newName, error: !newName });
  };
  const onClick = () => {
    if (file) {
      setFile(prev => ({ ...prev, value: null, error: true }));
    }
    input.current.click();
  };
  const onUpload = () => {
    if (!formError) {
      onCreate(file.value, name.value);
    }
  };
  const onClodeModal = () => {
    setFile(initialFile);
    setName(initialName);
    onClose();
  };
  return (
    <Overlay isOpen data-test-id="create-tsv-modal">
      <Modal
        title="Add custom audience"
        handleClose={onClodeModal}
        footer={
          <ListGroup
            direction="row"
            items={[
              <Loading isLoading={loading}>
                <ListGroup
                  direction="row"
                  halign="left"
                  items={[
                    <Button
                      key="0"
                      size="small"
                      disabled={formError}
                      onClick={onUpload}
                      data-test="new-tsv-submit"
                    >
                      Upload
                    </Button>,
                    <Button
                      key="1"
                      size="small"
                      variant="outlined"
                      onClick={onClodeModal}
                    >
                      Close
                    </Button>,
                  ]}
                />
              </Loading>,
            ]}
          />
        }
      >
        <Box mB="medium">
          <Text size="xbody" weight="semi-bold">
            Upload your audience file with their Twitter handles - one for each
            line - in the CSV format. The yellow circle indicates the file is
            being processed, once it disappears, the results are ready to
            review.
          </Text>
        </Box>
        <Box mB="medium">
          <Input
            placeholder="Name"
            data-test="new-tsv-name"
            size="small"
            value={name.value}
            error={name.error && name.touched}
            onChange={onChangeName}
            className={name.error ? 'invalid' : ''}
          />
        </Box>
        <StyledDragWrapper
          dragging={dragging}
          onDrop={e => {
            e.persist();
            e.preventDefault();
            const newFile = e.dataTransfer.files[0];
            setFile(prev => ({
              ...prev,
              touched: true,
              value: newFile || null,
              error: newFile?.type !== 'text/csv',
            }));
            setDragging(false);
          }}
          onDragEnter={() => {
            setDragging(true);
          }}
          onDragLeave={() => {
            setDragging(false);
          }}
          onDragOver={e => {
            e.persist();
            e.preventDefault();
            setDragging(true);
          }}
        >
          <StyledDragFileName>
            {file && (
              <Icon
                icon={file.error && file.touched ? 'remove' : 'done'}
                size="small"
                variant={file.error && file.touched ? 'error' : 'success'}
              />
            )}
            <Text size="xbody" weight="semi-bold">
              {file.value ? file.value.name : 'Drag and drop'}
            </Text>
          </StyledDragFileName>
          <Button
            size="small"
            weight="semi-bold"
            variant="subtle"
            style={{ textTransform: 'none' }}
            onClick={onClick}
          >
            {file
              ? 'Click here to change file'
              : 'Click here to select a .csv file'}
            <label htmlFor="tsv-file-upload" style={{ display: 'none' }}>
              File upload
            </label>
            <input
              id="tsv-file-upload"
              type="file"
              accept="text/csv"
              ref={input}
              style={{ display: 'none' }}
              data-test="new-tsv-input"
              onChange={e => {
                e.persist();
                e.preventDefault();
                const newFile = input.current.files[0];
                setFile(prev => ({
                  ...prev,
                  touched: true,
                  value: newFile || null,
                  error: newFile?.type !== 'text/csv',
                }));
                setDragging(false);
              }}
            />
          </Button>
        </StyledDragWrapper>
        <Box mT="medium">
          <Text
            size="xbody"
            weight="semi-bold"
            variant="error"
            data-test="new-tsv-error"
          >
            {error || formError}
          </Text>
        </Box>
      </Modal>
    </Overlay>
  );
};

export default CreateTsvModal;
